import type { CellClassParams, EditableCallback, EditableCallbackParams } from "ag-grid-community";
import { uniq } from "ramda";
import { isNumberFormat } from "widgets/utils";

import { type IDataTableQueryParams } from "@/api/WebApiClient.ts";
import { chooseUniqueColor, DISTINCT_COLORS, getColorFromTerm } from "@/app-utils/color.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type TGridSelModelIdentifier } from "@/app-utils/selection-model.ts";
import { compareStrings } from "@/app-utils/string.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import {
  type Column,
  type InputModifyOrganismInput,
  type InputOrganismPropertyInput,
  type InputOrganismPropertyTypeInput,
  type InputTableQuerySpec,
  type OrganismProperty,
  type OrganismPropertyType,
  type Table,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import { CELL_TYPE, COLUMN_DYNAMIC_TYPES, MAX_LENGTH } from "@/settings/settings.ts";
import { traverseAndCollect } from "@/ui-modules/core/grid/column-props-factory.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { PHENOTYPE_COMPARISON_CONFIG } from "../../ui-modules/core/grid/cells/editors/PhenotypeEditor.js";
import { type ICustomColDef } from "../../ui-modules/core/grid/DataTable.js";
import {
  ADD_DATA_EDITING_COLUMNS,
  CLEAR_EDITING_DATA,
  DATA_EDITING_VALUES_CHANGED,
  DELETE_ORG_PROPERTY_TYPES_FAILURE,
  LOAD_ALL_ORG_PROPERTY_TYPES_FAILURE,
  LOAD_ALL_ORG_PROPERTY_TYPES_REQUEST,
  LOAD_ALL_ORG_PROPERTY_TYPES_SUCCESS,
  LOAD_DATA_EDITING_COLUMNS_FAILURE,
  LOAD_DATA_EDITING_COLUMNS_REQUEST,
  LOAD_DATA_EDITING_COLUMNS_SUCCESS,
  LOAD_DATA_EDITING_VALUES_FAILURE,
  LOAD_DATA_EDITING_VALUES_REQUEST,
  LOAD_DATA_EDITING_VALUES_SUCCESS,
  LOAD_ORG_PROPERTY_TYPE_FAILURE,
  LOAD_ORG_PROPERTY_TYPE_REQUEST,
  SAVE_DATA_REQUEST,
  SAVE_DATA_SUCCESS,
  SAVE_ORG_PROPERTY_TYPE_FAILURE,
  SAVE_ORG_PROPERTY_TYPE_REQUEST,
  SAVE_ORG_PROPERTY_TYPE_SUCCESS,
  SELECT_ORG_PROPERTY_TYPE,
  STORE_ORG_PROPERTY_METADATA,
  VALIDATE_CELL_EDITING_DATA_COMPLETED,
  VALIDATE_CELL_EDITING_DATA_STARTED,
  VALIDATE_FULL_EDITING_DATA_COMPLETED,
  VALIDATE_FULL_EDITING_DATA_STARTED,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { fetchPhenotypeValues } from "./genome-browser.js";
import {
  buildDataTableQueryParamsForSpecificColumns,
  getTableDataSlice,
  getTableQuerySpecWithSelection,
  type IIdentifiable,
  type ITableColumn,
  setColumnsVisibility,
  triggerTableRefresh,
} from "./table-data.js";

export type TPhenotypeValue = string | number;

export interface IEditable<T = any> extends IIdentifiable {
  [colId: number]: T;
  locked?: boolean;
  editing?: Record<number, IEditCellMetadata>;
}

/**
 * State definition
 */
export interface IDataEditingState {
  isLoading: boolean;
  isValidating: boolean;
  tableQuerySpec: InputTableQuerySpec;
  columns: ITableColumn[];
  rowData: IEditable<TPhenotypeValue>[];

  orgPropertyValuesLookup: Dictionary<OrganismProperty[]>; // store the org properties by type, so that we can easily get the color
  orgPropertyTypes: OrganismPropertyType[];
  isLoadingOrgPropertyTypes: boolean;
}

const initialState: IDataEditingState = {
  isLoading: false,
  isValidating: false,
  tableQuerySpec: null,
  columns: [],
  rowData: [],

  orgPropertyValuesLookup: {},
  orgPropertyTypes: [], // list of all available org property types, used e.g. for the "select existing tag" dialog
  isLoadingOrgPropertyTypes: false,
};

export interface IEditCellChange<T = any> {
  rowId: TGridSelModelIdentifier;
  //data?: IEditable; // perhaps in some cases this is helpful ?
  newValue?: T;
  newColor?: string;
  column?: Partial<ITableColumn>;
}

// as DTO for the passing information to reducers of specific information
export interface IEditCellValidation {
  rowId: TGridSelModelIdentifier;
  column: Partial<ITableColumn>;
  validationMessage?: string;
}

// as stored in redux
export interface IEditCellMetadata {
  didChange: boolean;
  readOnly?: boolean;
  //columnField: string;
  validating?: boolean;
  validationError?: string;
}

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_DATA_EDITING_COLUMNS_REQUEST]: (
    state: IDataEditingState,
    tableQuerySpec: InputTableQuerySpec,
  ): IDataEditingState => {
    return {
      ...state,
      tableQuerySpec: tableQuerySpec,
      isLoading: true,
    };
  },

  [LOAD_DATA_EDITING_COLUMNS_FAILURE]: (state: IDataEditingState): IDataEditingState => {
    return {
      ...state,
      isLoading: false,
    };
  },

  [LOAD_DATA_EDITING_COLUMNS_SUCCESS]: (state: IDataEditingState, payload: Column[]): IDataEditingState => {
    return {
      ...state,
      columns: payload,
      isLoading: false,
    };
  },

  [ADD_DATA_EDITING_COLUMNS]: (state: IDataEditingState, columns: ITableColumn[]): IDataEditingState => {
    const updatedColumns: ITableColumn[] = columns.map((column) => ({ ...column, editable: true, hidden: false }));

    // case of updated columns, they stay in the same order in the existing list
    const gridColumns: ITableColumn[] = state.columns.map((gridColumn) => {
      const updated = updatedColumns.find((updatedColumn) => gridColumn.columnId === updatedColumn.columnId);
      return updated || gridColumn;
    });
    // case of new columns
    const newColumns = updatedColumns.filter((updatedColumn) => {
      return !gridColumns.find((gridColumn) => gridColumn.columnId === updatedColumn.columnId);
    });
    gridColumns.push(...newColumns);

    return {
      ...state,
      columns: gridColumns,
    };
  },

  [LOAD_DATA_EDITING_VALUES_REQUEST]: (state: IDataEditingState): IDataEditingState => {
    return {
      ...state,
      isLoading: true,
    };
  },

  [LOAD_DATA_EDITING_VALUES_SUCCESS]: (state: IDataEditingState, payload: IEditable[]): IDataEditingState => {
    payload.forEach((row: IEditable) => {
      const existingRow = state.rowData.find((existingEntity: IIdentifiable) => existingEntity.id === row.id);

      state.columns
        .filter((column: ITableColumn) => column.editable)
        .forEach((column: ITableColumn) => {
          //const editMetadataAccessor = `${getEditingMetadataAccessor({ tableColumn: column })}`;
          // make sure we don't overwrite already edited data
          // const existingRowMetadata: Partial<IEditCellMetadata> = existingRow?.editing get(existingRow, editMetadataAccessor);
          if (existingRow?.editing?.[column.columnId]?.didChange) {
            row[column.columnId] = existingRow[column.columnId];
            //  traverseAndCollect(existingRow, column.groupField || column.field); // we use the groupField pointing to the complex object (e.g. OrgProp or ProjectFile) or to the primitive
            //set(row, column.groupField || column.field, existingCellData);
          } else {
            // TODO depending on the type, we might need the groupField e.g. projectFiles ? or do we use a filesLookup ?
            const cellData = traverseAndCollect(row, /*column.groupField || */ column.field); // we use the groupField pointing to the complex object (e.g. OrgProp or ProjectFile) or to the primitive
            row[column.columnId] = cellData;

            const initialEditCellMetadata: IEditCellMetadata = {
              didChange: false,
            };

            row.editing = {
              ...row.editing,
              [column.columnId]: initialEditCellMetadata,
            };
            //set(row, editMetadataAccessor, initialEditCellMetadata);
          }
        });
    });

    return {
      ...state,
      rowData: payload,
      isLoading: false,
    };
  },

  [LOAD_DATA_EDITING_VALUES_FAILURE]: (state: IDataEditingState): IDataEditingState => {
    return {
      ...state,
      isLoading: false,
    };
  },

  [DATA_EDITING_VALUES_CHANGED]: (state: IDataEditingState, cellChanges: IEditCellChange[]): IDataEditingState => {
    if (!cellChanges) {
      return state;
    }

    let dataGrid: IEditable[] = state.rowData;
    const cellEditingMetadata: Partial<IEditCellMetadata> = { didChange: true };
    cellChanges.forEach((cellChange: IEditCellChange) => {
      dataGrid = setEditCellData(dataGrid, cellChange, cellEditingMetadata);
    });

    return {
      ...state,
      rowData: dataGrid,
    };
  },

  [VALIDATE_FULL_EDITING_DATA_STARTED]: (state: IDataEditingState): IDataEditingState => {
    return {
      ...state,
      isValidating: true,
    };
  },

  [VALIDATE_FULL_EDITING_DATA_COMPLETED]: (
    state: IDataEditingState,
    editCellValidations: IEditCellValidation[],
  ): IDataEditingState => {
    let rowData: IEditable[] = state.rowData;
    editCellValidations.forEach((editCellValidation) => {
      rowData = setEditCellValidation(rowData, editCellValidation.rowId, editCellValidation.column.columnId, {
        validating: false,
        validationError: editCellValidation.validationMessage,
      });
    });

    return {
      ...state,
      rowData: rowData,
      isValidating: false,
    };
  },

  [VALIDATE_CELL_EDITING_DATA_STARTED]: (
    state: IDataEditingState,
    editCellValidation: IEditCellValidation,
  ): IDataEditingState => {
    let rowData: IEditable[] = state.rowData;
    rowData = setEditCellValidation(rowData, editCellValidation.rowId, editCellValidation.column.columnId, {
      validating: true,
    });

    return {
      ...state,
      rowData: rowData,
    };
  },

  [VALIDATE_CELL_EDITING_DATA_COMPLETED]: (
    state: IDataEditingState,
    editCellValidation: IEditCellValidation,
  ): IDataEditingState => {
    let rowData: IEditable[] = state.rowData;
    rowData = setEditCellValidation(rowData, editCellValidation.rowId, editCellValidation.column.columnId, {
      validating: false,
      validationError: editCellValidation.validationMessage,
    });

    return {
      ...state,
      rowData: rowData,
    };
  },

  [CLEAR_EDITING_DATA]: (_state: IDataEditingState): IDataEditingState => {
    return { ...initialState };
  },

  [LOAD_ALL_ORG_PROPERTY_TYPES_REQUEST]: (state: IDataEditingState): IDataEditingState => {
    return {
      ...state,
      isLoadingOrgPropertyTypes: true,
    };
  },

  [LOAD_ALL_ORG_PROPERTY_TYPES_SUCCESS]: (
    state: IDataEditingState,
    payload: OrganismPropertyType[],
  ): IDataEditingState => {
    return {
      ...state,
      orgPropertyTypes: payload,
      isLoadingOrgPropertyTypes: false,
    };
  },

  [LOAD_ALL_ORG_PROPERTY_TYPES_FAILURE]: (state: IDataEditingState): IDataEditingState => {
    return {
      ...state,
      isLoadingOrgPropertyTypes: false,
    };
  },

  [STORE_ORG_PROPERTY_METADATA]: (
    state: IDataEditingState,
    payload: { id: string; values: OrganismProperty[] },
  ): IDataEditingState => {
    return {
      ...state,
      orgPropertyValuesLookup: {
        ...state.orgPropertyValuesLookup,
        [payload.id]: payload.values,
      },
    };
  },
});

/**
 * Action creators
 */

export function loadEditingColumns(
  tableSpec: InputTableQuerySpec,
  columnsEditingController: (column: Column[]) => Column[],
) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Column[]> => {
    const state: IAppState = getState();

    const selectionSpec: InputTableQuerySpec = {
      ...getTableQuerySpecWithSelection(getTableDataSlice(state.tableData, tableSpec)),
      pagination: null,
    };

    dispatch({
      type: LOAD_DATA_EDITING_COLUMNS_REQUEST,
      payload: selectionSpec,
    });

    const queryParams: IDataTableQueryParams = {
      tableQuerySpec: selectionSpec,
      withColumns: true,
    };
    return webApiClient
      .fetchTableData(queryParams)
      .then((table: Table) => {
        const editingColumns = columnsEditingController(table.columns);
        dispatch({
          type: LOAD_DATA_EDITING_COLUMNS_SUCCESS,
          payload: editingColumns,
        });
        return editingColumns;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATA_EDITING_COLUMNS_FAILURE,
          alertTitle: "Fetching of Columns for Editing Failed",
        }),
      );
  };
}

export function fetchColumnEditingMetadata(columns: ITableColumn[]) {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient) => {
    columns.forEach((column: ITableColumn) => {
      if (column.dynamicType === COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY && column.cellType === CELL_TYPE.STRING) {
        dispatch(valuesForOrganismPropertyType(column.columnId)).then((orgProperties: OrganismProperty[]) => {
          dispatch(updateOrganismPropertyMetadata(column.columnId, orgProperties));
        });
      }
    });
  };
}

export function updateOrganismPropertyMetadata(columnId: number, orgProperties) {
  return {
    type: STORE_ORG_PROPERTY_METADATA,
    payload: {
      id: columnId,
      values: orgProperties,
    },
  };
}

export function loadEditingData() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<IEditable[]> => {
    const state: IAppState = getState();
    const selectionSpec: InputTableQuerySpec = state.dataEditing.tableQuerySpec;

    const columns: ITableColumn[] = state.dataEditing.columns;

    dispatch({
      type: LOAD_DATA_EDITING_VALUES_REQUEST,
    });

    const queryParams: IDataTableQueryParams = Object.assign(
      buildDataTableQueryParamsForSpecificColumns(selectionSpec, columns),
      {
        withPageRows: true,
      },
    );

    return webApiClient
      .fetchTableData(queryParams, columns)
      .then((table: Table) => {
        dispatch({
          type: LOAD_DATA_EDITING_VALUES_SUCCESS,
          payload: table.pageRows,
        });

        dispatch(validateFullDataGrid());
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATA_EDITING_VALUES_FAILURE,
          //showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function setPhenotypeEditingGridChanges(cellChanges: IEditCellChange[]) {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const changes: IEditCellChange[] = cellChanges.map((cellChange) => {
      let newValue = editingCrossColumnsConversion(cellChange.column, cellChange.newValue);

      const orgPropertyValuesLookup = getState().dataEditing.orgPropertyValuesLookup;
      const existingValues = orgPropertyValuesLookup[cellChange.column.columnId];
      // add new value and corresponding color to the list of existing phenotype values
      if (cellChange.column.cellType === CELL_TYPE.STRING && newValue) {
        const existingValue = existingValues.find((existingValue) =>
          compareStrings(existingValue.value, newValue, PHENOTYPE_COMPARISON_CONFIG),
        );
        if (!existingValue) {
          const existingColors = uniq(existingValues.map((tag) => tag.color));
          const newColor = getNewPhenotypeColor(existingColors, newValue);
          dispatch(
            updateOrganismPropertyMetadata(cellChange.column.columnId, [
              ...(orgPropertyValuesLookup[cellChange.column.columnId] || []),
              { value: newValue, color: newColor },
            ]),
          );
        } else {
          newValue = existingValue.value; // take the originally stored voc term (in case the case/trimming is different)
        }
      }
      return {
        ...cellChange,
        newValue: newValue,
      };
    });

    dispatch({
      type: DATA_EDITING_VALUES_CHANGED,
      payload: changes,
    });

    dispatch(validateDataGridChanges(changes));
  };
}

function getValidationMessage(cellType: string, cellValue: any) {
  switch (cellType) {
    case CELL_TYPE.STRING:
      return textValidator(cellValue);
      break;
    case CELL_TYPE.NUMBER:
    case CELL_TYPE.INTEGER:
      return numberValidator(cellValue);
      break;
    case CELL_TYPE.STRING_LIST:
      return cellValue
        .map(textValidator)
        ?.filter((message) => message)
        .join("; ");
      break;
    case CELL_TYPE.NUMBER_LIST:
      return cellValue
        .map(numberValidator)
        ?.filter((message) => message)
        .join("; ");
    default:
      return null;
  }
}

function validateDataGridChanges(changes: IEditCellChange<OrganismProperty>[]) {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient) => {
    changes.forEach((change) => {
      const editCellValidation: IEditCellValidation = {
        rowId: change.rowId,
        column: change.column,
      };

      dispatch({
        type: VALIDATE_CELL_EDITING_DATA_STARTED,
        payload: editCellValidation,
      });

      // possible to do async stuff here

      // sync validation
      const validationMessage = getValidationMessage(change.column.cellType, change.newValue);

      editCellValidation.validationMessage = validationMessage;

      dispatch({
        type: VALIDATE_CELL_EDITING_DATA_COMPLETED,
        payload: editCellValidation,
      });
    });
  };
}

function validateFullDataGrid() {
  return (dispatch, getState: () => IAppState, _webApiClient: WebApiClient) => {
    const state = getState().dataEditing;
    dispatch({
      type: VALIDATE_FULL_EDITING_DATA_STARTED,
    });

    const validationErrors = [];
    state.rowData.forEach((data: IEditable, idx) => {
      state.columns
        .filter((column: ITableColumn) => column.editable)
        .forEach((column: ITableColumn) => {
          const value = data[column.columnId];
          //const value = traverseAndCollect(data, /*column.groupField || */ column.field); // we use the groupField pointing to the complex object (e.g. OrgProp or ProjectFile) or to the primitive
          const validationMessage = getValidationMessage(column.cellType, value);
          const editCellValidation: IEditCellValidation = {
            rowId: idx,
            column: column,
            validationMessage: validationMessage,
          };
          validationErrors.push(editCellValidation);
        });
    });

    return dispatch({
      type: VALIDATE_FULL_EDITING_DATA_COMPLETED,
      payload: validationErrors,
    });
  };
}

export function saveDataEditingChanges() {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IDataEditingState = getState().dataEditing;
    const rowData: IEditable[] = state.rowData;
    const editableColumns = state.columns.filter((column) => column.editable);

    const changes: InputModifyOrganismInput[] = [];

    rowData.forEach((data: IEditable) => {
      const editedColumns: ITableColumn[] = editableColumns.filter((col) => {
        return data.editing?.[col.columnId]?.didChange;
      });

      if (editedColumns.length) {
        const change: InputModifyOrganismInput = {
          organismId: data.id,
          organismProperties: [],
        };
        editedColumns.forEach((column: ITableColumn) => {
          if (column.dynamic) {
            if (column.dynamicType === COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY) {
              const editCellData = data[column.columnId];
              const organismProperties: OrganismProperty[] = state.orgPropertyValuesLookup[column.columnId];
              const organismProperty: OrganismProperty = organismProperties?.find(
                (orgProp) => orgProp.value === editCellData,
              );
              const input: InputOrganismPropertyInput = {
                orgPropertyTypeId: column.columnId,
                numValue:
                  column.cellType === CELL_TYPE.NUMBER || column.cellType === CELL_TYPE.INTEGER ? editCellData : null,
                value: column.cellType === CELL_TYPE.STRING ? editCellData : null,
                colour: organismProperty?.color, // only mandatory if phenotype is new
              };
              change.organismProperties.push(input);
            }
          }
        });
        changes.push(change);
      }
    });

    dispatch({
      type: SAVE_DATA_REQUEST,
    });

    return webApiClient.editOrganism(changes).then(() => {
      dispatch({
        type: SAVE_DATA_SUCCESS,
      });

      // make sure the newly added phenotypes are part of the visible columns
      dispatch(
        setColumnsVisibility(
          state.columns.map((col) => ({ ...col, editable: false })),
          false,
          state.tableQuerySpec,
        ),
      );
      // simply trigger the reload of the metadata and data, because the phenotype editing might affect the grid in case of active filtering
      dispatch(triggerTableRefresh(state.tableQuerySpec));

      // in case we are in the context of the genome viewer, we might need to refresh the phenotype values displayed in the variant track header
      dispatch(fetchPhenotypeValues());

      notify.info(
        {
          title: selMessage({ id: "saveDataEditing.successTitle" }),
          children: SelMessage({ id: "saveDataEditing.successMessage", values: { rowDataLength: rowData.length } }),
        },
        { autoClose: 10000 },
      );
    });
  };
}

export function clearEditData() {
  return {
    type: CLEAR_EDITING_DATA,
  };
}

export function loadAllOrgPropertyTypes() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_ALL_ORG_PROPERTY_TYPES_REQUEST,
    });
    return webApiClient
      .fetchAllOrgPropertyTypes()
      .then((orgPropertyTypes: OrganismPropertyType[]) => {
        dispatch({
          type: LOAD_ALL_ORG_PROPERTY_TYPES_SUCCESS,
          payload: orgPropertyTypes,
        });
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ALL_ORG_PROPERTY_TYPES_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function loadOrgPropertyType(orgPropertyTypeId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient) => {
    dispatch({
      type: LOAD_ORG_PROPERTY_TYPE_REQUEST,
    });
    return webApiClient
      .fetchOrgPropertyType(orgPropertyTypeId)
      .then((orgPropertyType: OrganismPropertyType) => {
        return orgPropertyType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ORG_PROPERTY_TYPE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function saveOrgPropertyType(payload: InputOrganismPropertyTypeInput) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<OrganismPropertyType> => {
    dispatch({ type: SAVE_ORG_PROPERTY_TYPE_REQUEST });

    return webApiClient
      .saveOrgPropertyType(payload)
      .then((orgPropertyType: OrganismPropertyType) => {
        dispatch({
          type: SAVE_ORG_PROPERTY_TYPE_SUCCESS,
          payload: orgPropertyType,
        });
        // consider the new column (modified ones are ignored)
        dispatch(addColumnsToEditingGrid([orgPropertyType.id]));
        return orgPropertyType;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SAVE_ORG_PROPERTY_TYPE_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function selectOrgPropertyTypes(orgPropertyTypes: OrganismPropertyType[]) {
  return (dispatch, _getState, _webApiClient: WebApiClient): Promise<OrganismPropertyType[]> => {
    dispatch({ type: SELECT_ORG_PROPERTY_TYPE });
    dispatch(addColumnsToEditingGrid(orgPropertyTypes.map((orgPropertyType) => orgPropertyType.id)));
    return Promise.resolve(orgPropertyTypes);
  };
}

export function addColumnsToEditingGrid(columnIds: number[]) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient) => {
    const state: IAppState = getState();

    const spec: InputTableQuerySpec = {
      ...state.dataEditing.tableQuerySpec,
      restrictions: null,
    };

    return Promise.all(columnIds.map((columnId) => webApiClient.fetchGridColumn(columnId, spec)))
      .then((columns: ITableColumn[]) => {
        dispatch({
          type: ADD_DATA_EDITING_COLUMNS,
          payload: columns,
        });
        dispatch(fetchColumnEditingMetadata(columns));
        dispatch(loadEditingData());
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_DATA_EDITING_COLUMNS_FAILURE,
          alertTitle: "Fetching of Columns Metadata for Editing Failed",
        }),
      );
  };
}

export function valuesForOrganismPropertyType(orgPropertyTypeId: number, searchTerm: string = null) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<OrganismProperty[]> => {
    return webApiClient.fetchValuesForOrganismPropertyType(orgPropertyTypeId, searchTerm).catch(
      createErrorHandler(dispatch, {
        actionType: DELETE_ORG_PROPERTY_TYPES_FAILURE,
        throwError: true,
      }),
    );
  };
}

/******  Utils *************/
// necessary for using the Excel like "enableFillHandle" feature (the alternative would be to restrict the "enableFillHandle" to the y axis (see ag-grid api) )
// also needed to handle the cell copy via clipboard (encoding everything as string)
export function editingCrossColumnsConversion(column: Partial<ITableColumn>, cellValue: any) {
  if (column.cellType === CELL_TYPE.STRING) {
    if (Array.isArray(cellValue)) {
      return cellValue.join(", ");
    }
  } else if (column.cellType === CELL_TYPE.STRING_LIST || column.cellType === CELL_TYPE.NUMBER_LIST) {
    if (!Array.isArray(cellValue)) {
      return [cellValue];
    }
  } else if (column.cellType === CELL_TYPE.BOOLEAN) {
    // make sure copy/paste operations are correctly handled for all possible types of boolean inputs, see SEL10-1546
    //
    // booleans as they are generated by manual editing in the cell (clicking)
    if (cellValue === true || cellValue === false || cellValue === null) {
      return cellValue;
    }
    // pasted-in values (strings, even for 0/1)
    if (cellValue.toUpperCase() === "TRUE" || cellValue === "1" || cellValue.toUpperCase() === "YES") {
      return true;
    }
    if (cellValue.toUpperCase() === "FALSE" || cellValue === "0" || cellValue.toUpperCase() === "NO") {
      return false;
    }
    // option to unset the boolean (blank Excel cell, leading to empty string)
    if (cellValue === "") {
      return null;
    }
  }
  // fallback
  return cellValue;
}

export function setEditCellData(
  rowData: IEditable[],
  cellChange: IEditCellChange<OrganismProperty>,
  cellMetadata: Partial<IEditCellMetadata>,
) {
  const column = cellChange.column;

  return rowData.map((row) => {
    if (row.id === cellChange.rowId) {
      return {
        ...row,
        [column.columnId]: cellChange.newValue,
        editing: {
          ...row.editing,
          [column.columnId]: {
            ...row.editing?.[column.columnId],
            ...cellMetadata,
          },
        },
      };
    }
    return row;
  });
}

export function setEditCellValidation(
  rowData: IEditable[],
  rowId: TGridSelModelIdentifier,
  columnId: number,
  cellMetadata: Partial<IEditCellMetadata>,
): IEditable[] {
  return rowData.map((row) => {
    if (row.id == rowId) {
      return {
        ...row,
        editing: {
          ...row.editing,
          [columnId]: {
            ...row.editing?.[columnId],
            ...cellMetadata,
          },
        },
      };
    }
    return row;
  });
}

export function editDataHasValidationError(params: CellClassParams): boolean {
  return !!params.data.editing?.[params.colDef.colId]?.validationError;
}

export const isCellEditable = (params: { data: IEditable; colDef: ICustomColDef }) => {
  return params.data.editing?.[params.colDef.colId]?.readOnly
    ? false
    : // we need to execute the editable callback if it exists (otherwise consider editable false)
      params.colDef.editable
      ? (params.colDef.editable as EditableCallback)(params as EditableCallbackParams)
      : false;
};
// used for cell coloring only
export const isCellReadOnly = (params: { data: IEditable; colDef: ICustomColDef }) => {
  return !isCellEditable(params);
};

const textValidator = (value: string) => {
  if (value?.length > MAX_LENGTH.ORGANISM_PROPERTY.NAME) {
    return `Max number of characters is ${MAX_LENGTH.ORGANISM_PROPERTY.NAME}`;
  }
  return;
};

const numberValidator = (value: string) => {
  if (value && !isNumberFormat(value)) {
    return `Please enter a number`;
  }
  return;
};

export function getNewPhenotypeColor(existingColors: string[], term?: string) {
  return getColorFromTerm(term) || chooseUniqueColor(DISTINCT_COLORS, existingColors);
}
